import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { ICompany, IOverview } from '@/types/Company'
import type { IMyCompany, IMyInvitation } from '@/types/User'

/** The user's companies and the selected one (spec §3.3: every page applies to it only). */
export const useCompanyStore = defineStore('company', () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const companies   = ref<IMyCompany[]>([])
    const invitations = ref<IMyInvitation[]>([])
    const company     = ref<ICompany | null>(null)
    const overview    = ref<IOverview | null>(null)
    const loaded      = ref(false)

    const companyId = computed(() => company.value?.id ?? '')
    const base      = computed(() => `/companies/${companyId.value}`)
    const isOwner   = computed(() => company.value?.my_role === 'owner' || company.value?.my_role === 'admin')

    const loadCompanies = async () => {
        const [c, i] = await Promise.all([
            fetchStore.fetch('/me/companies').get().json<IMyCompany[]>(),
            fetchStore.fetch('/me/invitations').get().json<IMyInvitation[]>(),
        ])
        companies.value = c.statusCode.value === 200 ? (c.data.value ?? []) : []
        invitations.value = i.statusCode.value === 200 ? (i.data.value ?? []) : []
    }

    const loadCompany = async (id: string): Promise<boolean> => {
        const { statusCode, data } = await fetchStore.fetch(`/companies/${id}`).get().json<ICompany>()
        if (statusCode.value === 200 && data.value) {
            company.value = data.value
            return true
        }
        return false
    }

    /** Picks the last used company, else the first membership. */
    const init = async () => {
        await loadCompanies()
        const last = appStore.me?.last_company_id
        let ok = false
        if (last) ok = await loadCompany(last)
        if (!ok && companies.value.length > 0) {
            const first = companies.value.find((c) => c.status !== 'blocked') ?? companies.value[0]
            if (first) ok = await select(first.id)
        }
        if (!ok) company.value = null
        loaded.value = true
    }

    const select = async (id: string): Promise<boolean> => {
        await fetchStore.fetch('/me/last-company').put({ company_id: id }).json()
        const ok = await loadCompany(id)
        if (ok && appStore.me) appStore.me.last_company_id = id
        overview.value = null
        if (ok) await loadOverview()
        return ok
    }

    const reloadCompany = async () => {
        if (companyId.value) await loadCompany(companyId.value)
    }

    const loadOverview = async () => {
        if (!companyId.value) return
        const { statusCode, data } = await fetchStore.fetch(`${base.value}/overview`).get().json<IOverview>()
        if (statusCode.value === 200 && data.value) overview.value = data.value
    }

    const reset = () => {
        companies.value = []
        invitations.value = []
        company.value = null
        overview.value = null
        loaded.value = false
    }

    return {
        companies, invitations, company, overview, loaded, companyId, base, isOwner,
        init, loadCompanies, select, reloadCompany, loadOverview, reset,
    }
})
